/**
 * The console API, as the browser sees it.
 *
 * Same-origin, carrying the `__Host-lagoon_admin` cookie automatically. There
 * is no Auth0 token in JavaScript here: the service exchanges the code, reads
 * the permissions, and issues an opaque session. Nothing worth stealing lives
 * in this process.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  if (init.body !== undefined) headers.set("content-type", "application/json");

  const response = await fetch(path, { ...init, credentials: "same-origin", headers });
  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApiError(
      response.status,
      readString(payload, "error") ?? "request_failed",
      readString(payload, "message") ?? `Request failed (${response.status})`,
    );
  }
  return payload as T;
}

function readString(value: unknown, key: string): string | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const found: unknown = Reflect.get(value, key);
  return typeof found === "string" ? found : undefined;
}

// --- shapes ----------------------------------------------------------------

export interface Me {
  email: string;
  subject: string;
  permissions: string[];
  can: { read: boolean; operate: boolean; administer: boolean };
}

export interface SearchSite {
  id: string;
  slug: string;
  name: string;
  ownerEmail: string;
  suspendedAt: string | null;
  publishedDeployId: string | null;
}

export interface SearchOwner {
  id: string;
  email: string;
  name: string | null;
  suspendedAt: string | null;
  siteCount: number;
}

/** One owner and everything they own. `GET /api/owners/:id`. */
export interface OwnerDetail {
  owner: {
    id: string;
    email: string;
    name: string | null;
    isStaff: boolean;
    /** False means they cannot publish. It looks identical to "cannot publish". */
    emailVerified: boolean;
    suspendedAt: string | null;
    suspendedReason: string | null;
    createdAt: string;
    /** Their personal organisation, which is what holds the quotas. */
    orgId: string;
    plan: string;
    quotaSites: number;
    quotaBytes: number;
  };
  sites: {
    id: string;
    slug: string;
    name: string;
    suspendedAt: string | null;
    publishedDeployId: string | null;
  }[];
}

export interface OwnerSuspension {
  id: string;
  email: string;
  suspendedAt: string | null;
}

export interface OwnerQuota {
  id: string;
  orgId: string;
  quotaSites: number;
  quotaBytes: number;
}

/** One row of `platform_flags`. `GET /api/flags`. */
export interface PlatformFlag {
  key: string;
  /** `jsonb`, so anything. The console edits it in the shape it is already in. */
  value: unknown;
  description: string | null;
  updatedBy: string | null;
  updatedAt: string;
}

export interface SiteDetail {
  site: {
    id: string;
    slug: string;
    name: string;
    publicMode: boolean;
    publishedDeployId: string | null;
    suspendedAt: string | null;
    suspendedReason: string | null;
    createdAt: string;
    ownerId: string;
    ownerEmail: string;
    ownerSuspendedAt: string | null;
  };
  releases: { id: string; state: string; createdAt: string; error: string | null }[];
  allowlist: { email: string; createdAt: string }[];
  liveSessions: number;
}

export interface OrgSearchRow {
  id: string;
  name: string;
  kind: "personal" | "team";
  plan: string;
  suspendedAt: string | null;
  createdAt: string;
  memberCount: number;
  siteCount: number;
}

export interface OrgDetail {
  org: {
    id: string;
    name: string;
    kind: "personal" | "team";
    plan: string;
    quotaSites: number;
    quotaBytes: number;
    seats: number;
    maxioCustomerId: string | null;
    maxioSubscriptionId: string | null;
    suspendedAt: string | null;
    suspendedReason: string | null;
    createdAt: string;
  };
  members: {
    ownerId: string;
    email: string;
    name: string | null;
    role: string;
    ownerSuspendedAt: string | null;
    createdAt: string;
  }[];
  sites: {
    id: string;
    slug: string;
    name: string;
    ownerEmail: string;
    suspendedAt: string | null;
    suspendedReason: string | null;
    publishedDeployId: string | null;
  }[];
}

/** What billing knows about one organisation. `GET /api/orgs/:id/billing`. */
export interface OrgBilling {
  orgId: string;
  plan: string;
  /** The catalog row for `plan`, or null when the string is not a plan we sell. */
  catalog: {
    code: string;
    name: string;
    selfServe: boolean;
    entitlements: Record<string, unknown>;
  } | null;
  entitlements: Record<string, unknown>;
  state: "trialing" | "active" | "past_due" | "delinquent" | "ended" | "unknown";
  stateAt: string | null;
  dunningAt: string | null;
  trialEndsAt: string | null;
  maxio: {
    customerId: string | null;
    subscriptionId: string | null;
    /** Null when billing is unconfigured. Render nothing rather than a dead link. */
    customerUrl: string | null;
    subscriptionUrl: string | null;
  };
  events: {
    id: number;
    eventId: string;
    eventType: string;
    receivedAt: string;
    processedAt: string | null;
    error: string | null;
    subscriptionState: string | null;
  }[];
  override: EntitlementOverride | null;
  history: {
    id: string;
    entitlements: Record<string, number | boolean>;
    expiresAt: string | null;
    reason: string;
    actorEmail: string | null;
    createdAt: string;
    revokedAt: string | null;
    revokedReason: string | null;
  }[];
}

export interface EntitlementOverride {
  id: string;
  entitlements: Record<string, number | boolean>;
  expiresAt: string | null;
  reason: string;
  actorEmail: string | null;
  createdAt: string;
  /** False for an override whose expiry has passed but which nothing has lifted. */
  inForce: boolean;
}

export interface OverrideResult {
  orgId: string;
  plan: string;
  entitlements: Record<string, unknown>;
  override: {
    id: string;
    entitlements: Record<string, number | boolean>;
    expiresAt: string | null;
    reason: string;
    createdAt: string;
  } | null;
  changed: boolean;
}

export interface ProvisionedOrg {
  id: string;
  name: string;
  plan: string;
  owner: { id: string; email: string; created: boolean };
  /** False when the invitation email did not send. The organisation exists. */
  notified: boolean;
}

export interface OrgSuspension {
  id: string;
  name: string;
  suspendedAt: string | null;
  sitesChanged: string[];
  sitesLeftServing: string[];
}

export interface AuditEvent {
  id: number;
  occurredAt: string;
  actorType: string;
  actorEmail: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  siteId: string | null;
  reason: string | null;
  beforeState: unknown;
  afterState: unknown;
  ip: string | null;
}

export interface Health {
  sites: number;
  suspended: number;
  owners: number;
  liveViewerSessions: number;
  auditEvents24h: number;
  /** Releases screening has queued for review and nobody has dealt with. */
  screeningQueue: number;
}

/** One release in the screening review queue. `GET /api/screening/queue`. */
export interface ScreeningRelease {
  deployId: string;
  siteId: string;
  slug: string;
  siteName: string;
  ownerEmail: string;
  scanState: "pending" | "clean" | "flagged" | "blocked" | "error";
  createdAt: string;
  /** Is this the release the site is currently serving? */
  published: boolean;
  findingCount: number;
  rules: string[];
  reviewedAt: string | null;
  reviewedBy: string | null;
}

export interface ScreeningFinding {
  id: string;
  path: string | null;
  rule: string;
  severity: "block" | "flag" | "info";
  detail: unknown;
  createdAt: string;
}

export interface ScreeningReview {
  deploy: ScreeningRelease;
  findings: ScreeningFinding[];
}

export interface ScreeningActionResult {
  deployId: string;
  siteId: string;
  slug: string;
  scanState: string;
  reviewedAt: string;
  /** True when the action took a live release off the air. */
  unpublished: boolean;
}

/** A name the platform will not hand out, or watches for on a page. */
export interface DenylistEntry {
  id: string;
  kind: DenylistKind;
  term: string;
  note: string | null;
  createdBy: string | null;
  createdAt: string;
}

export type DenylistKind = "slug" | "brand";

export interface AuditQuery {
  action?: string;
  actor?: string;
  siteId?: string;
  before?: number;
  limit?: number;
}

export const api = {
  me: () => request<Me>("/api/me"),
  health: () => request<Health>("/api/health"),

  /**
   * `siteTotal` and `ownerTotal` are how many matched, not how many came back.
   * The tables say "25 of 412" from them, because a page that is exactly the
   * limit long is otherwise indistinguishable from a complete answer.
   */
  search: (q: string) =>
    request<{
      sites: SearchSite[];
      owners: SearchOwner[];
      siteTotal: number;
      ownerTotal: number;
    }>(`/api/search?q=${encodeURIComponent(q)}`),

  site: (id: string) => request<SiteDetail>(`/api/sites/${id}`),

  owner: (id: string) => request<OwnerDetail>(`/api/owners/${id}`),

  suspendOwner: (id: string, reason: string) =>
    request<OwnerSuspension>(`/api/owners/${id}/suspend`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  unsuspendOwner: (id: string, reason: string) =>
    request<OwnerSuspension>(`/api/owners/${id}/unsuspend`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  setOwnerQuota: (id: string, body: { quotaSites?: number; quotaBytes?: number; reason: string }) =>
    request<OwnerQuota>(`/api/owners/${id}/quota`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** An empty `q` is a browse: the most recently created organisations. */
  orgs: (q: string) =>
    request<{ orgs: OrgSearchRow[]; total: number }>(`/api/orgs?q=${encodeURIComponent(q)}`),

  org: (id: string) => request<OrgDetail>(`/api/orgs/${id}`),

  provisionOrg: (body: { name: string; ownerEmail: string; plan?: string; reason: string }) =>
    request<ProvisionedOrg>("/api/orgs", { method: "POST", body: JSON.stringify(body) }),

  orgBilling: (id: string) => request<OrgBilling>(`/api/orgs/${id}/billing`),

  overrideEntitlements: (
    id: string,
    body: {
      entitlements: Record<string, number | boolean>;
      expiresAt?: string;
      reason: string;
    },
  ) =>
    request<OverrideResult>(`/api/orgs/${id}/entitlement-override`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  clearOverride: (id: string, reason: string) =>
    request<OverrideResult>(`/api/orgs/${id}/entitlement-override/clear`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  suspendOrg: (id: string, reason: string) =>
    request<OrgSuspension>(`/api/orgs/${id}/suspend`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  unsuspendOrg: (id: string, reason: string) =>
    request<OrgSuspension>(`/api/orgs/${id}/unsuspend`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  audit: (query: AuditQuery = {}) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== "") params.set(key, String(value));
    }
    return request<{ events: AuditEvent[]; nextBefore: number | null }>(
      `/api/audit?${params.toString()}`,
    );
  },

  auditActions: () => request<{ actions: string[] }>("/api/audit/actions"),

  suspendSite: (id: string, reason: string) =>
    request(`/api/sites/${id}/suspend`, { method: "POST", body: JSON.stringify({ reason }) }),

  unsuspendSite: (id: string, reason: string) =>
    request(`/api/sites/${id}/unsuspend`, { method: "POST", body: JSON.stringify({ reason }) }),

  forceRollback: (id: string, deployId: string, reason: string) =>
    request(`/api/sites/${id}/force-rollback`, {
      method: "POST",
      body: JSON.stringify({ deployId, reason }),
    }),

  forceLogout: (id: string, reason: string) =>
    request<{ sessionsRevoked: number }>(`/api/sites/${id}/force-logout`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  removeViewer: (id: string, email: string, reason: string) =>
    request<{ removed: boolean; sessionsRevoked: number }>(`/api/sites/${id}/allowlist/remove`, {
      method: "POST",
      body: JSON.stringify({ email, reason }),
    }),

  denylist: (kind?: DenylistKind) =>
    request<{ entries: DenylistEntry[] }>(
      kind === undefined ? "/api/denylist" : `/api/denylist?kind=${kind}`,
    ),

  addDenylistTerm: (body: { kind: DenylistKind; term: string; note?: string; reason: string }) =>
    request<DenylistEntry>("/api/denylist", { method: "POST", body: JSON.stringify(body) }),

  removeDenylistTerm: (id: string, reason: string) =>
    request<DenylistEntry>(`/api/denylist/${id}/remove`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  screeningQueue: (includeReviewed = false) =>
    request<{ releases: ScreeningRelease[] }>(
      `/api/screening/queue${includeReviewed ? "?includeReviewed=true" : ""}`,
    ),

  screeningReview: (deployId: string) =>
    request<ScreeningReview>(`/api/screening/deploys/${deployId}`),

  clearScreening: (deployId: string, reason: string) =>
    request<ScreeningActionResult>(`/api/screening/deploys/${deployId}/clear`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  blockScreening: (deployId: string, reason: string) =>
    request<ScreeningActionResult>(`/api/screening/deploys/${deployId}/block`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  flags: () => request<{ flags: PlatformFlag[] }>("/api/flags"),

  setFlag: (key: string, value: unknown, reason: string) =>
    request<{ key: string; value: unknown }>(`/api/flags/${encodeURIComponent(key)}`, {
      method: "PUT",
      body: JSON.stringify({ value, reason }),
    }),

  signOut: () => request<{ status: string }>("/auth/logout", { method: "POST" }),
};
